import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Check,
  HelpCircle,
  Info,
  Loader2,
  Lock,
  PackageCheck,
  PackageX,
  RefreshCw,
  Search,
  Sparkles,
  TrendingDown,
} from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { normalizeLabel } from '../features/pos-v2/PosV2HorsStockDrawer';

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** Seuil de remise « normale » : au-delà, l’excédent est compté en perte. */
const REMISE_SEUIL_PCT = 20;

const isHorsStockLine = (line) => {
  if (!line) return false;
  if (line.is_hors_stock === true) return true;
  const ref = String(line.produit_ref || '').trim().toUpperCase();
  if (ref === 'HORS-STOCK' || ref.startsWith('HORS-STOCK-') || ref.startsWith('HORS-STOCK ')) return true;
  return Boolean(line.matched_stock_ref);
};

/** Contrat : `lines` (nouveau) ou `items` (document vente brut). */
const getSaleLines = (sale) => {
  const raw = Array.isArray(sale?.lines) ? sale.lines : (Array.isArray(sale?.items) ? sale.items : []);
  return raw.map((l, i) => ({ ...l, index: Number.isInteger(l?.index) ? l.index : i }));
};

/** Contrat : `{ items: [...] }` (nouveau), `{ ventes: [...] }` ou tableau nu. */
const extractPending = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.ventes)) return data.ventes;
  return [];
};

/** Normalise un article catalogue (suggestion serveur ou résultat /stock). */
const normalizeArticle = (a, magasinId) => {
  if (!a || !a.reference) return null;
  const q = a.quantite;
  const prix = Number(a.prix_vente);
  return {
    reference: String(a.reference),
    designation: a.designation || String(a.reference),
    prix_vente: Number.isFinite(prix) && prix > 0 ? prix : null,
    quantite: q === null || q === undefined || q === '' ? null : Number(q),
    magasin_id: a.magasin_id || magasinId || null,
  };
};

const buildLineForm = (line, magasinId, t) => {
  const hors = isHorsStockLine(line);
  const qty = Math.max(1, Number(line.quantite) || 1);
  const prix = Number(line.prix_unitaire) || 0;
  const lineTotal = Number(line.prix_total);
  const suggestions = (Array.isArray(line.suggestions) ? line.suggestions : [])
    .map((s) => normalizeArticle(s, magasinId))
    .filter(Boolean);

  // Référence proposée par le serveur : on la retrouve dans les suggestions si possible.
  const hintRef = line.matched_stock_ref ? String(line.matched_stock_ref) : '';
  let matched = null;
  if (hintRef) {
    const wanted = normalizeLabel(hintRef);
    const fromSuggestions = suggestions.find((s) => normalizeLabel(s.reference) === wanted) || null;
    const hintPrice = Number(line.matched_prix_catalogue);
    matched = fromSuggestions || {
      reference: hintRef,
      designation: line.designation || hintRef,
      prix_vente: Number.isFinite(hintPrice) && hintPrice > 0 ? hintPrice : null,
      quantite: null, // inconnu tant que non résolu via /stock
      magasin_id: magasinId || null,
      unresolved: true,
    };
  }

  return {
    index: line.index,
    designation: line.designation || line.produit_ref || t('adminHorsStock.lineDefaultDesignation'),
    produit_ref: line.produit_ref || '',
    prix, // prix unitaire réellement encaissé (lecture seule)
    qty, // quantité réellement encaissée (lecture seule)
    total: Number.isFinite(lineTotal) && lineTotal > 0 ? lineTotal : prix * qty,
    hors,
    suggestions,
    existed: hors ? (matched ? 'oui' : 'verifier') : 'keep',
    matched,
    search: '',
    results: [],
    searching: false,
  };
};

const buildSaleForm = (sale, t) => ({
  note: '',
  lines: getSaleLines(sale).map((l) => buildLineForm(l, sale.magasin_id, t)),
});

/** Montants de la vente, tels qu’encaissés (jamais recalculés côté page). */
const saleAmounts = (sale, form) => {
  const linesTotal = (form?.lines || []).reduce((s, lf) => s + (Number(lf.total) || 0), 0);
  const sousTotalRaw = Number(sale?.sous_total);
  const sousTotal = Number.isFinite(sousTotalRaw) && sousTotalRaw > 0 ? sousTotalRaw : linesTotal;
  const remisePctRaw = Number(sale?.remise_pct) || 0;
  const remiseMontantRaw = Number(sale?.remise_montant) || 0;
  const remise = remiseMontantRaw > 0
    ? Math.min(remiseMontantRaw, sousTotal)
    : (remisePctRaw > 0 ? sousTotal * (remisePctRaw / 100) : 0);
  const remisePct = sousTotal > 0 ? (remise / sousTotal) * 100 : 0;
  const totalRaw = Number(sale?.total);
  const payeRaw = Number(sale?.montant_paye);
  const encaisse = Number.isFinite(totalRaw) && totalRaw > 0
    ? totalRaw
    : (Number.isFinite(payeRaw) && payeRaw > 0 ? payeRaw : Math.max(0, sousTotal - remise));
  const remiseExcessive = Math.max(0, remise - sousTotal * (REMISE_SEUIL_PCT / 100));
  return { sousTotal, remise, remisePct, encaisse, remiseExcessive };
};

/** Perte estimée d’une ligne rattachée : (prix catalogue − prix vendu) × quantité, jamais négative. */
const lineLoss = (lf) => {
  if (!lf?.hors || lf.existed !== 'oui' || !lf.matched?.reference) return 0;
  const cat = Number(lf.matched.prix_vente);
  if (!(cat > 0)) return 0;
  return Math.max(0, cat - lf.prix) * lf.qty;
};

const lineBlocksConfirm = (lf) => {
  if (!lf?.hors) return false;
  if (lf.existed === 'verifier') return true;
  if (lf.existed === 'oui' && !lf.matched?.reference) return true;
  return false;
};

const errDetail = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') return d.message || d.detail || JSON.stringify(d);
  return err?.message || fallback;
};

const Badge = ({ tone = 'amber', children }) => {
  const tones = {
    amber: 'border-amber-500/40 bg-amber-950/40 text-amber-200',
    green: 'border-emerald-500/40 bg-emerald-950/40 text-emerald-200',
    red: 'border-red-500/40 bg-red-950/40 text-red-300',
    gray: 'border-white/15 bg-white/5 text-gray-300',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wide ${tones[tone] || tones.gray}`}>
      {children}
    </span>
  );
};

const StockHint = ({ quantite }) => {
  const { t } = useLanguage();
  if (quantite === null || quantite === undefined || Number.isNaN(quantite)) {
    return <span className="text-gray-400">{t('adminHorsStock.stockUnknown')}</span>;
  }
  return <span className={quantite > 0 ? 'text-emerald-300' : 'text-amber-300'}>{t('adminHorsStock.stockCount', { count: quantite })}</span>;
};

/** Carte article (suggestion serveur ou résultat de recherche), cliquable. */
const ArticleCard = ({ article, selected, onSelect, testId }) => {
  const { t, formatCurrency } = useLanguage();
  const eur = (n) => formatCurrency(Number(n) || 0);
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`w-full text-left rounded-lg border px-3 py-2 flex items-center justify-between gap-2 transition-colors ${
        selected
          ? 'border-emerald-500/60 bg-emerald-950/40 text-emerald-50'
          : 'border-white/10 bg-white/5 hover:bg-white/10 text-gray-200'
      }`}
      data-testid={testId}
    >
      <span className="min-w-0">
        <span className="block text-sm truncate">{article.designation}</span>
        <span className="block text-[11px] text-gray-500 font-mono truncate">{article.reference}</span>
      </span>
      <span className="text-right shrink-0 text-[11px] leading-tight">
        <span className="block text-white tabular-nums">{article.prix_vente > 0 ? eur(article.prix_vente) : '—'}</span>
        <StockHint quantite={article.quantite} />
        {selected && (
          <span className="text-emerald-300 mt-0.5 flex items-center justify-end gap-1"><Check size={10} /> {t('adminHorsStock.selected')}</span>
        )}
      </span>
    </button>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

/** Direction — rectifie les ventes hors-stock déjà encaissées avant leur écriture dans l’historique. */
export default function AdminHorsStockPendingPage() {
  const { api } = useAuth();
  const { t, formatCurrency, formatNumber, formatDate } = useLanguage();
  const [items, setItems] = useState([]);
  const [forms, setForms] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState([]);
  const searchTimers = useRef({});

  // Formatage dépendant de la langue active (remplace les anciens formateurs fr-FR / € codés en dur).
  const eur = (n) => formatCurrency(Number(n) || 0);
  const pct = (n) => `${formatNumber(Number(n) || 0, { maximumFractionDigits: 1 })} %`;
  const formatWhen = (iso) => {
    if (!iso) return '—';
    try {
      return formatDate(iso, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch {
      return String(iso).slice(0, 16);
    }
  };

  const patchLine = useCallback((ref, index, patch) => {
    setForms((prev) => {
      const form = prev[ref];
      if (!form) return prev;
      const lines = form.lines.map((lf) => {
        if (lf.index !== index) return lf;
        return typeof patch === 'function' ? patch(lf) : { ...lf, ...patch };
      });
      return { ...prev, [ref]: { ...form, lines } };
    });
  }, []);

  const patchForm = useCallback((ref, patch) => {
    setForms((prev) => (prev[ref] ? { ...prev, [ref]: { ...prev[ref], ...patch } } : prev));
  }, []);

  /** Résout prix catalogue / stock d’une référence proposée par le serveur mais absente des suggestions. */
  const resolveStockRef = useCallback(async (reference, magasinId) => {
    try {
      const res = await api.get('/stock', { params: { q: reference, magasin_id: magasinId || undefined } });
      const rows = Array.isArray(res.data) ? res.data : [];
      const wanted = normalizeLabel(reference);
      const exact = rows.find((a) => normalizeLabel(a.reference) === wanted) || null;
      return exact ? normalizeArticle(exact, magasinId) : null;
    } catch {
      return null;
    }
  }, [api]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/pos/v2/hors-stock/pending', { params: { limit: 100 } });
      const list = extractPending(res.data).filter((s) => s && s.ref_vente);
      setItems(list);
      const nextForms = {};
      list.forEach((sale) => { nextForms[sale.ref_vente] = buildSaleForm(sale, t); });
      setForms(nextForms);
      // Résolution asynchrone des références serveur non couvertes par les suggestions.
      await Promise.all(list.flatMap((sale) => nextForms[sale.ref_vente].lines
        .filter((lf) => lf.matched?.unresolved)
        .map(async (lf) => {
          const resolved = await resolveStockRef(lf.matched.reference, sale.magasin_id);
          if (!resolved) return;
          patchLine(sale.ref_vente, lf.index, (cur) => (
            cur.matched?.reference === lf.matched.reference
              ? { ...cur, matched: { ...resolved, prix_vente: cur.matched.prix_vente || resolved.prix_vente } }
              : cur
          ));
        })));
    } catch (err) {
      setError(errDetail(err, t('adminHorsStock.loadError')));
    } finally {
      setLoading(false);
    }
  }, [api, resolveStockRef, patchLine, t]);

  useEffect(() => { load(); }, [load]);

  /* ---------------- recherche catalogue (autre article) ---------------- */

  const runSearch = useCallback(async (ref, index, q, magasinId) => {
    const query = String(q || '').trim();
    if (query.length < 2) {
      patchLine(ref, index, { results: [], searching: false });
      return;
    }
    patchLine(ref, index, { searching: true });
    try {
      const res = await api.get('/stock', { params: { q: query, magasin_id: magasinId || undefined } });
      const rows = (Array.isArray(res.data) ? res.data : [])
        .filter((a) => a && !a.is_archived && a.is_active !== false)
        .slice(0, 8)
        .map((a) => normalizeArticle(a, magasinId))
        .filter(Boolean);
      patchLine(ref, index, { results: rows, searching: false });
    } catch {
      patchLine(ref, index, { results: [], searching: false });
    }
  }, [api, patchLine]);

  const onSearchChange = (sale, lf, value) => {
    const key = `${sale.ref_vente}#${lf.index}`;
    patchLine(sale.ref_vente, lf.index, { search: value });
    if (searchTimers.current[key]) clearTimeout(searchTimers.current[key]);
    searchTimers.current[key] = setTimeout(() => runSearch(sale.ref_vente, lf.index, value, sale.magasin_id), 350);
  };

  useEffect(() => {
    const timers = searchTimers.current;
    return () => { Object.values(timers).forEach(clearTimeout); };
  }, []);

  const attachArticle = (sale, lf, article) => {
    patchLine(sale.ref_vente, lf.index, {
      matched: article,
      existed: 'oui',
      results: [],
      search: '',
    });
  };

  const setExisted = (sale, lf, value) => {
    patchLine(sale.ref_vente, lf.index, (cur) => ({
      ...cur,
      existed: value,
      results: value === 'oui' ? cur.results : [],
    }));
  };

  /* ---------------- action unique : valider ---------------- */

  const validateSale = async (sale) => {
    const ref = sale.ref_vente;
    const form = forms[ref];
    if (!form) return;
    if (form.lines.some(lineBlocksConfirm)) {
      toast.error(t('adminHorsStock.lineStillUnresolved'));
      return;
    }
    const amounts = saleAmounts(sale, form);
    const localLoss = form.lines.reduce((s, lf) => s + lineLoss(lf), 0) + amounts.remiseExcessive;
    setBusy(ref);
    setError('');
    try {
      const payload = {
        items: form.lines.map((lf) => (
          lf.hors && lf.existed === 'oui' && lf.matched?.reference
            ? { index: lf.index, action: 'attach', produit_ref: lf.matched.reference }
            : { index: lf.index, action: 'keep' }
        )),
        note: form.note || '',
      };
      const res = await api.post(`/pos/v2/hors-stock/${encodeURIComponent(ref)}/confirm`, payload);
      const data = res?.data || {};
      const serverTotal = Number(data.total);
      const shownTotal = Number.isFinite(serverTotal) && serverTotal > 0 ? serverTotal : amounts.encaisse;
      const serverLoss = Number(data.pertes?.total);
      const shownLoss = Number.isFinite(serverLoss) ? serverLoss : localLoss;
      if (shownLoss > 0.005) {
        toast.success(t('adminHorsStock.saleValidatedWithLoss', { ref, total: eur(shownTotal), loss: eur(shownLoss) }));
      } else {
        toast.success(t('adminHorsStock.saleValidatedNoLoss', { ref, total: eur(shownTotal) }));
      }
      setDone((prev) => [
        { ref, total: shownTotal, perte: shownLoss, when: new Date().toISOString() },
        ...prev,
      ].slice(0, 10));
      setItems((prev) => prev.filter((s) => s.ref_vente !== ref));
      setForms((prev) => {
        const next = { ...prev };
        delete next[ref];
        return next;
      });
    } catch (err) {
      const msg = errDetail(err, t('adminHorsStock.validationFailed'));
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy('');
    }
  };

  const pendingCount = items.length;
  const anyBusy = Boolean(busy);

  /* ---------------- rendu ---------------- */

  const renderLine = (sale, lf) => {
    const blocked = lineBlocksConfirm(lf);

    if (!lf.hors) {
      return (
        <div key={lf.index} className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm text-white truncate">{lf.designation}</p>
            <p className="text-[11px] text-gray-500 font-mono truncate">
              {lf.produit_ref || '—'} · {lf.qty} × {eur(lf.prix)}
            </p>
          </div>
          <div className="text-right shrink-0">
            <Badge tone="gray">{t('adminHorsStock.catalogBadge')}</Badge>
            <p className="text-sm text-white tabular-nums mt-1">{eur(lf.total)}</p>
          </div>
        </div>
      );
    }

    const loss = lineLoss(lf);
    const catalogPrice = Number(lf.matched?.prix_vente) || 0;
    const selectedRef = lf.matched?.reference ? normalizeLabel(lf.matched.reference) : '';
    const isSelected = (a) => Boolean(selectedRef) && normalizeLabel(a.reference) === selectedRef;

    return (
      <div
        key={lf.index}
        className={`rounded-lg border px-3 py-3 space-y-3 ${blocked ? 'border-amber-500/40 bg-amber-950/10' : 'border-white/10 bg-black/20'}`}
        data-testid={`hs-line-${sale.ref_vente}-${lf.index}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm text-white truncate">{lf.designation}</p>
            <p className="text-[11px] text-gray-500 font-mono truncate">
              {lf.produit_ref || 'HORS-STOCK'} · {t('adminHorsStock.cashedLineInfo', { qty: lf.qty, price: eur(lf.prix) })}
            </p>
          </div>
          <div className="text-right shrink-0">
            <Badge tone="amber"><PackageX size={10} /> {t('adminHorsStock.horsStockBadge')}</Badge>
            <p className="text-sm text-white tabular-nums mt-1">{eur(lf.total)}</p>
          </div>
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1.5">{t('adminHorsStock.articleExistedQuestion')}</p>
          <div className="flex flex-wrap gap-2">
            {[
              { v: 'oui', label: t('adminHorsStock.optionLoss'), Icon: PackageCheck, tone: 'emerald' },
              { v: 'non', label: t('adminHorsStock.optionNonExistent'), Icon: PackageX, tone: 'gray' },
              { v: 'verifier', label: t('adminHorsStock.optionToVerify'), Icon: HelpCircle, tone: 'amber' },
            ].map(({ v, label, Icon, tone }) => {
              const active = lf.existed === v;
              const activeCls = {
                emerald: 'border-emerald-500/60 bg-emerald-950/40 text-emerald-100',
                gray: 'border-white/40 bg-white/15 text-white',
                amber: 'border-amber-500/60 bg-amber-950/40 text-amber-100',
              }[tone];
              const cls = active ? activeCls : 'border-white/10 bg-white/5 text-gray-300 hover:bg-white/10';
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => setExisted(sale, lf, v)}
                  aria-pressed={active}
                  className={`min-h-[36px] px-3 rounded-lg border text-xs font-semibold flex items-center gap-1.5 ${cls}`}
                  data-testid={`hs-existed-${v}-${sale.ref_vente}-${lf.index}`}
                >
                  <Icon size={13} /> {label}
                </button>
              );
            })}
          </div>
        </div>

        {lf.existed === 'oui' && (
          <div className="space-y-3">
            {lf.matched?.reference ? (
              <div
                className="rounded-lg border border-emerald-500/30 bg-emerald-950/20 px-3 py-2 flex items-start justify-between gap-3"
                data-testid={`hs-matched-${sale.ref_vente}-${lf.index}`}
              >
                <div className="min-w-0 text-[11px] text-gray-200 space-y-0.5">
                  <p className="text-[10px] uppercase tracking-wide text-emerald-300 font-semibold">{t('adminHorsStock.attachedArticle')}</p>
                  <p className="text-sm text-white truncate">{lf.matched.designation}</p>
                  <p className="font-mono">{t('adminHorsStock.refLabel', { ref: lf.matched.reference })}</p>
                  <p>
                    {t('adminHorsStock.catalogPriceLabel')}{' '}
                    <span className="text-white tabular-nums">{catalogPrice > 0 ? eur(catalogPrice) : t('adminHorsStock.unknown')}</span>
                    {' · '}
                    {t('adminHorsStock.soldLabel')} <span className="text-white tabular-nums">{eur(lf.prix)}</span>
                    {' · '}
                    <StockHint quantite={lf.matched.quantite} />
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] uppercase tracking-wide text-gray-500">{t('adminHorsStock.estimatedLoss')}</p>
                  {loss > 0.005 ? (
                    <p className="text-sm font-semibold text-amber-300 tabular-nums flex items-center justify-end gap-1">
                      <TrendingDown size={13} /> {eur(loss)}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 tabular-nums">{eur(0)}</p>
                  )}
                  {catalogPrice > 0 && (
                    <p className="text-[10px] text-gray-500 tabular-nums">
                      ({eur(catalogPrice)} − {eur(lf.prix)}) × {lf.qty}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-[11px] text-amber-300 flex items-center gap-1.5">
                <AlertTriangle size={12} /> {t('adminHorsStock.selectCatalogRef')}
              </p>
            )}

            <div>
              <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1.5 flex items-center gap-1.5">
                <Sparkles size={11} /> {t('adminHorsStock.catalogSuggestions')}
              </p>
              {lf.suggestions.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {lf.suggestions.map((a) => (
                    <ArticleCard
                      key={`sug-${a.reference}-${a.magasin_id || ''}`}
                      article={a}
                      selected={isSelected(a)}
                      onSelect={() => attachArticle(sale, lf, a)}
                      testId={`hs-suggestion-${sale.ref_vente}-${lf.index}-${a.reference}`}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-gray-500">{t('adminHorsStock.noSuggestion')}</p>
              )}
            </div>

            <div className="relative">
              <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1.5">{t('adminHorsStock.otherCatalogArticle')}</p>
              <div className="flex items-center gap-2 rounded-lg bg-black/30 border border-white/10 px-3">
                <Search size={14} className="text-gray-500 shrink-0" />
                <input
                  className="w-full py-2.5 bg-transparent text-sm text-white outline-none"
                  placeholder={t('adminHorsStock.searchPlaceholder')}
                  value={lf.search}
                  onChange={(e) => onSearchChange(sale, lf, e.target.value)}
                  data-testid={`hs-search-${sale.ref_vente}-${lf.index}`}
                />
                {lf.searching && <Loader2 size={14} className="animate-spin text-gray-400" />}
              </div>
              {lf.results.length > 0 && (
                <ul className="mt-1 rounded-lg border border-white/10 bg-[#0f1117] divide-y divide-white/5 max-h-56 overflow-y-auto">
                  {lf.results.map((a) => (
                    <li key={`res-${a.reference}-${a.magasin_id || ''}`}>
                      <button
                        type="button"
                        onClick={() => attachArticle(sale, lf, a)}
                        className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-center justify-between gap-2"
                      >
                        <span className="min-w-0">
                          <span className="block text-sm text-white truncate">{a.designation}</span>
                          <span className="block text-[11px] text-gray-500 font-mono truncate">{a.reference}</span>
                        </span>
                        <span className="text-right shrink-0 text-[11px]">
                          <span className="block text-white tabular-nums">{a.prix_vente > 0 ? eur(a.prix_vente) : '—'}</span>
                          <StockHint quantite={a.quantite} />
                          {a.magasin_id ? <span className="text-gray-500"> · {a.magasin_id}</span> : null}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {!lf.searching && lf.search.trim().length >= 2 && lf.results.length === 0 && (
                <p className="mt-1 text-[11px] text-gray-500">{t('adminHorsStock.noArticleFound')}</p>
              )}
            </div>
          </div>
        )}

        {lf.existed === 'non' && (
          <p className="text-[11px] text-gray-400 flex items-center gap-1.5">
            <PackageX size={12} /> {t('adminHorsStock.freeArticleInfo')}
          </p>
        )}

        {lf.existed === 'verifier' && (
          <p className="text-[11px] text-amber-300 flex items-center gap-1.5">
            <AlertTriangle size={12} /> {t('adminHorsStock.toVerifyInfo')}
          </p>
        )}
      </div>
    );
  };

  const renderSale = (sale) => {
    const ref = sale.ref_vente;
    const form = forms[ref];
    if (!form) return null;
    const amounts = saleAmounts(sale, form);
    const blocked = form.lines.some(lineBlocksConfirm);
    const nbVerifier = form.lines.filter((lf) => lf.hors && lf.existed === 'verifier').length;
    const nbSansRef = form.lines.filter((lf) => lf.hors && lf.existed === 'oui' && !lf.matched?.reference).length;
    const lossLines = form.lines
      .map((lf) => ({ lf, loss: lineLoss(lf) }))
      .filter(({ loss }) => loss > 0.005);
    const lossHorsStock = lossLines.reduce((s, { loss }) => s + loss, 0);
    const lossTotal = lossHorsStock + amounts.remiseExcessive;
    const hasLoss = lossTotal > 0.005;

    return (
      <li key={sale.id || ref} className="rounded-xl border border-white/10 bg-[#151820] p-4 space-y-4" data-testid={`hs-sale-${ref}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-mono text-sm text-white">{ref}</p>
              <Badge tone="amber">{t('adminHorsStock.toValidateBadge')}</Badge>
              <Badge tone="green"><Lock size={10} /> {t('adminHorsStock.cashedBadge')}</Badge>
              {sale.statut_paiement && <Badge tone="gray">{sale.statut_paiement}</Badge>}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {formatWhen(sale.date_vente)} · {sale.vendeur || '—'} · {sale.magasin_id || '—'}
              {sale.client_nom ? ` · ${sale.client_nom}` : ''}
              {sale.mode_paiement ? ` · ${sale.mode_paiement}` : ''}
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[10px] uppercase tracking-wide text-gray-500">{t('adminHorsStock.cashedAmountLabel')}</p>
            <p className="text-lg font-bold text-white tabular-nums">{eur(amounts.encaisse)}</p>
          </div>
        </div>

        <div className="space-y-2">
          {form.lines.map((lf) => renderLine(sale, lf))}
        </div>

        <div className="rounded-lg border border-white/10 bg-black/25 px-3 py-2 text-sm space-y-1">
          <div className="flex justify-between text-gray-400">
            <span>{t('adminHorsStock.subtotal')}</span>
            <span className="tabular-nums">{eur(amounts.sousTotal)}</span>
          </div>
          {amounts.remise > 0.005 && (
            <div className={`flex justify-between ${amounts.remiseExcessive > 0.005 ? 'text-amber-300' : 'text-emerald-400'}`}>
              <span>{t('adminHorsStock.discountLabel')} ({pct(amounts.remisePct)})</span>
              <span className="tabular-nums">−{eur(amounts.remise)}</span>
            </div>
          )}
          <div className="flex justify-between items-baseline text-lg font-bold text-white">
            <span>{t('adminHorsStock.historyTotal')}</span>
            <span className="tabular-nums">{eur(amounts.encaisse)}</span>
          </div>
          <p className="text-[11px] text-gray-500 flex items-center gap-1.5">
            <Lock size={11} /> {t('adminHorsStock.amountLockedInfo')}
          </p>
        </div>

        <div
          className={`rounded-lg border px-3 py-2 text-sm space-y-1 ${hasLoss ? 'border-amber-500/40 bg-amber-950/15' : 'border-white/10 bg-black/25'}`}
          data-testid={`hs-perte-${ref}`}
        >
          <p className={`text-[10px] uppercase tracking-wide font-semibold flex items-center gap-1.5 ${hasLoss ? 'text-amber-300' : 'text-gray-500'}`}>
            <TrendingDown size={12} /> {t('adminHorsStock.lossesPreview')}
          </p>
          {lossLines.map(({ lf, loss }) => (
            <div key={`loss-${lf.index}`} className="flex justify-between gap-3 text-amber-200">
              <span className="min-w-0 truncate">
                {t('adminHorsStock.estimatedLoss')} — {lf.matched.designation}
                <span className="text-gray-500 font-mono"> ({lf.matched.reference})</span>
              </span>
              <span className="tabular-nums shrink-0">{eur(loss)}</span>
            </div>
          ))}
          {amounts.remiseExcessive > 0.005 && (
            <div className="flex justify-between gap-3 text-amber-200">
              <span>{t('adminHorsStock.remiseExcessiveLabel', { seuil: REMISE_SEUIL_PCT, pct: pct(amounts.remisePct) })}</span>
              <span className="tabular-nums shrink-0">{eur(amounts.remiseExcessive)}</span>
            </div>
          )}
          {hasLoss ? (
            <>
              <div className="flex justify-between items-baseline font-bold text-red-300 border-t border-amber-500/20 pt-1 mt-1">
                <span>{t('adminHorsStock.lossRecorded')}</span>
                <span className="tabular-nums text-base">{eur(lossTotal)}</span>
              </div>
              <p className="text-[11px] text-amber-200/80">{t('adminHorsStock.lossSavedInfo')}</p>
            </>
          ) : (
            <p className="text-[11px] text-gray-500">
              {t('adminHorsStock.noLossYet')}
              {nbVerifier + nbSansRef > 0 ? t('adminHorsStock.noLossYetSuffix') : '.'}
            </p>
          )}
        </div>

        <label className="block text-[11px] text-gray-400">
          {t('adminHorsStock.noteLabel')}
          <input
            value={form.note}
            onChange={(e) => patchForm(ref, { note: e.target.value })}
            placeholder={t('adminHorsStock.notePlaceholder')}
            className="mt-1 w-full px-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-sm text-white min-h-[44px]"
            data-testid={`hs-note-${ref}`}
          />
        </label>

        <div className="flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            disabled={anyBusy || blocked}
            title={blocked ? t('adminHorsStock.blockedTitle') : undefined}
            onClick={() => validateSale(sale)}
            className="min-h-[44px] px-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`hs-confirm-${ref}`}
          >
            {busy === ref ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}
            {t('adminHorsStock.validateButton')}
          </button>
        </div>
        {blocked && (
          <p className="text-[11px] text-amber-300 text-right">
            {t('adminHorsStock.validationBlocked')}{' '}
            {nbVerifier > 0 ? t('adminHorsStock.blockedVerifierCount', { n: nbVerifier }) : ''}
            {nbVerifier > 0 && nbSansRef > 0 ? ' · ' : ''}
            {nbSansRef > 0 ? t('adminHorsStock.blockedNoRefCount', { n: nbSansRef }) : ''}.
          </p>
        )}
      </li>
    );
  };

  const legend = useMemo(() => (
    <div className="rounded-xl border border-white/10 bg-black/25 p-3 text-[11px] text-gray-400 space-y-1.5">
      <p className="text-gray-200 font-semibold flex items-center gap-1.5"><Info size={13} /> {t('adminHorsStock.legendTitle')}</p>
      <p>{t('adminHorsStock.legendIntro')}</p>
      <p>
        <span className="text-emerald-300">{t('adminHorsStock.legendYesWord')}</span> {t('adminHorsStock.legendYesDesc')}{' '}
        <span className="text-white">{t('adminHorsStock.legendNonWord')}</span> {t('adminHorsStock.legendNonDesc')}{' '}
        <span className="text-amber-300">{t('adminHorsStock.optionToVerify')}</span> {t('adminHorsStock.legendVerifierDesc')}
      </p>
      <p>
        <span className="text-amber-300">{t('adminHorsStock.legendPertesWord')}</span> {t('adminHorsStock.legendPertesDesc', { seuil: REMISE_SEUIL_PCT })}
      </p>
    </div>
  ), [t]);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-4 space-y-4 text-white">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <PackageX className="text-orange-400" />
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                {t('adminHorsStock.pageTitle')}
                {pendingCount > 0 && <Badge tone="amber">{pendingCount}</Badge>}
              </h1>
              <p className="text-sm text-gray-400">{t('adminHorsStock.pageSubtitle')}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading || anyBusy}
            className="min-h-[40px] px-3 rounded-lg bg-white/10 hover:bg-white/15 text-gray-200 text-sm flex items-center gap-1.5 disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> {t('adminHorsStock.refresh')}
          </button>
        </div>

        {legend}

        {error && <p className="text-sm text-red-300 bg-red-950/40 border border-red-500/30 rounded-lg px-3 py-2">{error}</p>}

        {loading && items.length === 0 ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gray-400" /></div>
        ) : items.length === 0 ? (
          <p className="text-gray-400 text-sm py-8">{t('adminHorsStock.emptyState')}</p>
        ) : (
          <ul className="space-y-4">
            {items.map(renderSale)}
          </ul>
        )}

        {done.length > 0 && (
          <div className="rounded-xl border border-white/10 bg-black/25 p-3 space-y-2" data-testid="hs-done">
            <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">{t('adminHorsStock.processedThisSession')}</p>
            <ul className="space-y-1">
              {done.map((d) => (
                <li key={`${d.ref}-${d.when}`} className="flex items-center justify-between gap-2 text-xs">
                  <span className="font-mono text-gray-300">{d.ref}</span>
                  <span className="flex items-center gap-2">
                    <span className="tabular-nums text-gray-400">{eur(d.total)}</span>
                    {d.perte > 0.005 && (
                      <span className="tabular-nums text-amber-300">{t('adminHorsStock.lossShortLabel')} {eur(d.perte)}</span>
                    )}
                    <Badge tone="green"><Check size={10} /> {t('adminHorsStock.validatedBadge')}</Badge>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Layout>
  );
}
